/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: SaveFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/6/1 @ 1634
 * ---
 * Description:
 */

import {EditorFeature} from '../common/EditorFeature';
import {action, computed, makeObservable, observable} from 'mobx';
import {Editor} from '../../observables/Editor';
import {mleDomainGqlClient} from '../../../api/domain';
import {UPDATE_LABEL} from '../../../api/domain/mutations.gql';
import {Metadata, MetaGroup, MetaKey, MpesDocument, MpesInstanceManager} from '@revodigital/mpes';
import {MetadataGroupKeys, MetadataKeys} from '../../../common/enums';

export const SAVE_FNAME = 'save';

export class SaveFeature extends EditorFeature<void> {
    constructor(parent: Editor) {
        super(SAVE_FNAME, parent);

        this._isSaving = false;
        makeObservable(this);
    }

    @observable
    private _isSaving: boolean;

    @computed
    public get isSaving(): boolean {
        return this._isSaving;
    }

    /**
     * Gestisce il salvataggio. Codifica il contenuto dell'etichetta
     * tramite MPES e effettua l'upload sul server.
     */
    public async encodeAndUpload(): Promise<boolean> {
        let document;

        try {
            document = await this.encode();
        } catch (e) {
            console.error("Unable to encode the document ", e);
            return false;
        }

        try {
            this._isSaving = true;
            await mleDomainGqlClient.mutate({
                mutation: UPDATE_LABEL,
                variables: {
                    id: this.sourcePointer.projectData.labelId,
                    title: this.sourcePointer.projectData.title,
                    bodyContent: JSON.stringify(document)
                }
            });
            this._isSaving = false;

            return true;
        } catch (e) {
            console.error("Unable to save ", e);
            return false;
        }
    }

    /**
     * Codifica l'etichetta in un documento MPES
     * @param options
     */
    public async encode(options: { updateSavingStatus?: boolean, removeActiveStatus?: boolean } = {
        updateSavingStatus: false,
        removeActiveStatus: false
    }): Promise<MpesDocument> {
        if (options.updateSavingStatus)
            this._isSaving = true;

        // Formatto i metadati
        const metadata = new Metadata();
        metadata.addGroup(MetaGroup.init(MetadataGroupKeys.TEXT_BASED,
            [MetaKey.init<string>(MetadataKeys.MEASURE_UNIT,
                this.sourcePointer.projectData.projectOptions.measureUnit)]));

        // Write active revision
        if (options.removeActiveStatus === false)
            this.sourcePointer.recipeSwitcher.writeToMetadata(metadata);

        const encodedDocument = await MpesInstanceManager
            .getEncoderInstance()
            .encodeDocument(
                {
                    header: {
                        title: this.sourcePointer.projectData.title
                    },
                    canvasprops: {
                        width: this.sourcePointer.projectData.labelWidth,
                        height: this.sourcePointer.projectData.labelHeight,
                        showGrid: false,
                        colorMode: parseInt(this.sourcePointer.projectData.projectOptions.colorMode)
                    },
                    content: {
                        data: this.sourcePointer.pamelaStage__nonObservable.getLayers()
                    },
                    metadata,
                    dataRefs: this.sourcePointer.dataOriginFeature.dataRefs
                },
            );

        if (options.updateSavingStatus)
            this._isSaving = false;

        return encodedDocument;
    }

    @action
    public setIsSaving(value: boolean) {
        this._isSaving = value;
    }

    init(): boolean {
        return true;
    }

    destroy(): boolean {
        return true;
    }
}